export type MediaMemberKind =
  | "video"
  | "embeddedAudio"
  | "audio"
  | "waveformProxy"
  | "previewProxy";

export type MediaFamilyOrigin =
  | "audioOnly"
  | "videoOnly"
  | "videoWithEmbeddedAudio"
  | "linkedMedia";

export type EditorialBindingMode =
  | "linked"
  | "audioOnly"
  | "videoOnly"
  | "overrideAudio";

export type WitnessBindingMode = EditorialBindingMode | "unbound";

export type BindingMode = WitnessBindingMode;

export type AuditionMode = "source" | "clip" | "sequence";

export type EditorialBindingSource = "default" | "explicit";

export type MediaUnionKind = "audio" | "video" | "image" | "unknown";

export type MediaProxyKind = "timeline_proxy" | "scrub_proxy" | "waveform_proxy" | "preview_proxy";

export type MediaProxyTier = "ultra_low" | "low" | "medium" | "high";

export type MediaProxyStatus = "missing" | "queued" | "generating" | "ready" | "stale" | "failed" | "offline";

export type MediaProxyTimecodeParity = "verified" | "unverified" | "broken";

export type MediaOfflineState = "online" | "offline" | "missing" | "relinked" | "partial";

export type MediaAnalysisState = "idle" | "queued" | "running" | "ready" | "failed" | "partial";

export interface ProxyDescriptor {
  id: string;
  kind: MediaProxyKind;
  tier: MediaProxyTier;
  status: MediaProxyStatus;
  uri?: string;
  resolution?: string;
  codec?: string;
  colorTransform?: string;
  timecodeParity?: MediaProxyTimecodeParity;
  createdAt?: number;
  updatedAt?: number;
}

export interface MetadataHistoryEntry {
  id?: string;
  layer?: "source" | "normalized" | "semantic" | "user" | "system";
  at?: number;
  actor?: string;
  summary?: string;
  patch?: Record<string, unknown>;
}

export interface TranscodeHistoryEntry {
  id?: string;
  at?: number;
  tool?: string;
  sourceProxyId?: string;
  outputProxyId?: string;
  summary?: string;
  metadata?: Record<string, unknown>;
}

export interface ClipMediaMetadata {
  clipId: string;
  durationSec?: number;
  mediaUrl?: string;
  kind?: MediaUnionKind;
  assetId?: string;
  sourceAssetId?: string;
  sourceHash?: string;
  containerFormat?: string;
  schemaVersion?: string;
  ingestId?: string;
  storagePath?: string;
  sourceLabel?: string;
  fileName?: string;
  sourceFileName?: string;
  title?: string;
  scene?: string;
  take?: number | string;
  camera?: string;
  familyId?: string;
  originType?: MediaFamilyOrigin;
  hasPlayableAudio?: boolean;
  sampleRate?: number;
  channelCount?: number;
  channelLayout?: string;
  audioBitDepth?: number;
  audioCodec?: string;
  frameRate?: number;
  nativeWidth?: number;
  nativeHeight?: number;
  pixelAspectRatio?: number;
  displayAspectRatio?: number;
  colorSpace?: string;
  transferFunction?: string;
  hdrFormat?: string;
  interlaced?: boolean;
  videoBitDepth?: number;
  sourceInSec?: number;
  sourceOutSec?: number;
  timecodeStart?: string;
  dropFrame?: boolean;
  reelName?: string;
  streamLayout?: string;
  importSource?: string;
  ingestTimestamp?: number;
  metadataHistory?: MetadataHistoryEntry[];
  transcodeHistory?: TranscodeHistoryEntry[];
  offlineState?: MediaOfflineState;
  growingFile?: boolean;
  analysisState?: MediaAnalysisState;
  metadataRichnessScore?: number;
  decodePerformanceScore?: number;
  proxyStatus?: MediaProxyStatus;
  preferredTimelineProxyId?: string;
  preferredScrubProxyId?: string;
  proxies?: ProxyDescriptor[];
  metadataVault?: Record<string, unknown>;
  sourceMetadata?: Record<string, unknown>;
  semanticMetadata?: Record<string, unknown>;
  userMetadata?: Record<string, unknown>;
  systemMetadata?: Record<string, unknown>;
  /** @deprecated Use proxies with kind=waveform_proxy instead. */
  waveformProxyUrl?: string;
  /** @deprecated Use proxies with kind=preview_proxy or timeline_proxy instead. */
  previewProxyUrl?: string;
  metadata?: Record<string, unknown>;
}

export interface MediaUnionClipLike {
  id?: string;
  clipId?: string;
  trackId?: string;
  track?: string;
  name?: string;
  kind?: string;
  title?: string;
  label?: string;
  fileName?: string;
  filename?: string;
  assetName?: string;
  mediaName?: string;
  requestId?: string;
  mediaId?: string;
  mediaUrl?: string;
  previewUrl?: string;
  url?: string;
  src?: string;
  muted?: boolean;
  media?: { id?: string; url?: string };
  startSec?: number;
  start?: number;
  endSec?: number;
  end?: number;
  durSec?: number;
  dur?: number;
}

export interface MediaMember {
  id: string;
  familyId: string;
  kind: MediaMemberKind;
  sourceAssetId: string;
  label: string;
  durationSec: number;
  sampleRate?: number;
  channelLayout?: string;
  frameRate?: number;
  createdFromMemberId?: string;
  isPlayable: boolean;
  isVisible: boolean;
  metadata?: Record<string, unknown>;
  mediaUrl?: string;
  requestId?: string;
  clipIds: string[];
}

export interface MediaFamily {
  id: string;
  primaryLabel: string;
  originType: MediaFamilyOrigin;
  memberIds: string[];
  preferredVideoMemberId?: string;
  preferredAudioMemberId?: string;
  createdAt: number;
  clipIds: string[];
  identityKey: string;
}

export interface EditorialClipBinding {
  clipId: string;
  familyId: string;
  videoMemberId?: string;
  audioMemberId?: string;
  bindingMode: EditorialBindingMode;
  createdAt: number;
  updatedAt: number;
  source: EditorialBindingSource;
}

export interface ResolvedAudioPlayback {
  clipId: string;
  familyId: string;
  audioMemberId: string;
  startSec: number;
  endSec: number;
  gainDb?: number;
  muted?: boolean;
  label?: string;
  sourceAssetId?: string;
  memberKind?: MediaMemberKind;
}

export interface ResolvedVideoPlayback {
  clipId: string;
  familyId: string;
  videoMemberId: string;
  startSec: number;
  endSec: number;
  visible: boolean;
  label?: string;
  sourceAssetId?: string;
}

export interface PlaybackProjection {
  playheadSec: number;
  activeVideo: ResolvedVideoPlayback[];
  activeAudio: ResolvedAudioPlayback[];
  focusClipId?: string;
  focusFamilyId?: string;
  familyLabel?: string;
  bindingMode?: WitnessBindingMode;
  bindingSource?: EditorialBindingSource | "none";
  hasLinkedAudio: boolean;
  hasLinkedVideo: boolean;
  activeAudioMemberId?: string;
  activeVideoMemberId?: string;
  activeAudioClipId?: string;
  activeVideoClipId?: string;
  activeAudioDurationSec?: number;
  activeVideoDurationSec?: number;
  witnessLabel?: string;
  resolvedMemberSummary?: string;
}

export interface MediaBindingFamilyOption {
  id: string;
  label: string;
  clipCount: number;
  audioMemberCount: number;
  videoMemberCount: number;
  originType: MediaFamilyOrigin;
}

export interface MediaBindingEditorState {
  focusClipId?: string;
  bindingMode: WitnessBindingMode;
  bindingSource: EditorialBindingSource | "none";
  selectedFamilyId?: string;
  familyLabel?: string;
  selectedFamily?: MediaFamily;
  families: MediaFamily[];
  members: MediaMember[];
  familyOptions: MediaBindingFamilyOption[];
  audioMembers: MediaMember[];
  videoMembers: MediaMember[];
  preferredAudioMemberId?: string;
  preferredVideoMemberId?: string;
  hasLinkedAudio: boolean;
  hasLinkedVideo: boolean;
  witnessLabel?: string;
  resolvedMemberSummary?: string;
}

export interface DerivedMediaRegistry {
  families: MediaFamily[];
  members: MediaMember[];
  familyIdByClipId: Record<string, string>;
}

export interface ImportWitnessCapsuleSummary {
  clipId: string;
  familyId?: string;
  mediaType: "audio" | "video" | "image";
  label: string;
  witnessLabel?: string;
  resolvedMemberSummary?: string;
  familyClipCount?: number;
  durationSec: number;
  currentTimeSec?: number;
  status: "idle" | "ready" | "playing" | "muted" | "error";
  sourceId: string;
  tickerText?: string;
  tickerTone?: "blue" | "orange" | "red";
  bindingMode?: WitnessBindingMode;
  hasLinkedAudio: boolean;
  hasLinkedVideo: boolean;
}

function formatWitnessTimecode(sec: number | undefined): string {
  const safe = typeof sec === "number" && Number.isFinite(sec) ? Math.max(0, sec) : 0;
  const mm = Math.floor(safe / 60);
  const ss = Math.floor(safe % 60)
    .toString()
    .padStart(2, "0");
  return `${mm}:${ss}`;
}

function formatWitnessMetric(value: number | undefined, unit: string, maxFractionDigits = 2): string | undefined {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return undefined;
  return `${value.toLocaleString(undefined, {
    minimumFractionDigits: value % 1 === 0 ? 0 : 1,
    maximumFractionDigits: maxFractionDigits,
  })} ${unit}`;
}

function buildWitnessEditorialCue(args: {
  mediaType: "audio" | "video" | "image";
  atPlayhead: boolean;
  currentTimeSec?: number;
  leadOffsetSec: number;
  tailRemainingSec?: number;
}): string | undefined {
  const { mediaType, atPlayhead, currentTimeSec, leadOffsetSec, tailRemainingSec } = args;

  if (atPlayhead) {
    const introWindowSec = mediaType === "image" ? 1.5 : 1.1;
    const tailWindowSec = mediaType === "image" ? 2.5 : 2;

    if ((currentTimeSec ?? 0) <= introWindowSec) {
      return mediaType === "audio" ? "Cue entry live" : mediaType === "image" ? "Still enters live" : "Opening frames live";
    }

    if (typeof tailRemainingSec === "number" && Number.isFinite(tailRemainingSec) && tailRemainingSec <= tailWindowSec) {
      return mediaType === "audio"
        ? `Tail in ${formatWitnessTimecode(tailRemainingSec)}`
        : mediaType === "image"
          ? `Hold ends in ${formatWitnessTimecode(tailRemainingSec)}`
          : `Cut ends in ${formatWitnessTimecode(tailRemainingSec)}`;
    }

    return mediaType === "audio" ? "Bed holding at playhead" : mediaType === "image" ? "Still holding at playhead" : "Cut holding at playhead";
  }

  if (leadOffsetSec > 0 && leadOffsetSec <= 5) {
    return mediaType === "audio"
      ? `Cue in ${formatWitnessTimecode(leadOffsetSec)}`
      : mediaType === "image"
        ? `Still cues in ${formatWitnessTimecode(leadOffsetSec)}`
        : `Shot cues in ${formatWitnessTimecode(leadOffsetSec)}`;
  }

  return undefined;
}

function buildImportWitnessTicker(args: {
  mediaType: "audio" | "video" | "image";
  witnessLabel?: string;
  resolvedMemberSummary?: string;
  familyClipCount?: number;
  bindingMode: WitnessBindingMode;
  originType?: MediaFamilyOrigin;
  hasLinkedAudio: boolean;
  hasLinkedVideo: boolean;
  hasPlayableAudio: boolean;
  hasMediaUrl: boolean;
  frameRate?: number;
  sampleRate?: number;
  channelLayout?: string;
  durationSec: number;
  atPlayhead: boolean;
  isPlaying: boolean;
  isMuted: boolean;
  currentTimeSec?: number;
  startSec: number;
  leadOffsetSec: number;
  tailRemainingSec?: number;
}): { text?: string; tone: "blue" | "orange" | "red" } {
  const {
    mediaType,
    witnessLabel,
    resolvedMemberSummary,
    familyClipCount,
    bindingMode,
    originType,
    hasLinkedAudio,
    hasLinkedVideo,
    hasPlayableAudio,
    hasMediaUrl,
    frameRate,
    sampleRate,
    channelLayout,
    durationSec,
    atPlayhead,
    isPlaying,
    isMuted,
    currentTimeSec,
    startSec,
    leadOffsetSec,
    tailRemainingSec,
  } = args;

  const witnessLabelSummary = witnessLabel ? witnessLabel.replace(/\s*•\s*/g, " / ") : undefined;
  const memberSummaryLabel = resolvedMemberSummary;
  const familyClipCountLabel =
    typeof familyClipCount === "number" && Number.isFinite(familyClipCount) && familyClipCount > 1
      ? `${familyClipCount} clips in family`
      : undefined;
  const durationLabel = durationSec > 0 ? `${formatWitnessTimecode(durationSec)} span` : undefined;
  const activeLabel = atPlayhead
    ? isPlaying
      ? `Live ${formatWitnessTimecode(currentTimeSec)}`
      : `At playhead ${formatWitnessTimecode(currentTimeSec)}`
    : `Starts ${formatWitnessTimecode(startSec)}`;
  const editorialCueLabel = buildWitnessEditorialCue({
    mediaType,
    atPlayhead,
    currentTimeSec,
    leadOffsetSec,
    tailRemainingSec,
  });

  if (mediaType === "video") {
    const audioRoutingLabel = hasLinkedAudio
      ? "Linked audio armed"
      : hasPlayableAudio
        ? "Embedded audio"
        : "Silent cut";
    const readinessLabel = hasMediaUrl ? "Frame/share/export ready" : "Conform required";
    const frameRateLabel = formatWitnessMetric(frameRate, "fps");
    const modeLabel =
      bindingMode === "overrideAudio"
        ? "Audio swap locked to picture"
        : bindingMode === "videoOnly"
          ? "Silent cut route"
          : bindingMode === "linked"
            ? "Editorial A/V witness"
            : originType === "videoWithEmbeddedAudio"
              ? "Embedded soundtrack witness"
              : "Direct picture witness";
      const alertLabel = !hasMediaUrl
        ? "High alert: direct media missing"
        : !hasLinkedAudio && !hasPlayableAudio
          ? "High alert: no audio path"
          : bindingMode === "videoOnly"
            ? "Watch sync: visual-only route"
            : undefined;

    return {
        text: [
          witnessLabelSummary,
          memberSummaryLabel,
          familyClipCountLabel,
          modeLabel,
          alertLabel,
          audioRoutingLabel,
          frameRateLabel,
          durationLabel,
          editorialCueLabel,
          activeLabel,
          readinessLabel,
        ]
        .filter(Boolean)
        .join("  •  "),
        tone: !hasMediaUrl || (!hasLinkedAudio && !hasPlayableAudio)
          ? "red"
          : bindingMode === "videoOnly" || bindingMode === "overrideAudio"
            ? "orange"
            : "blue",
    };
  }

  if (mediaType === "audio") {
    const sampleRateLabel = formatWitnessMetric(sampleRate, "Hz", 0);
      const layoutLabel = channelLayout ? `${channelLayout} bus` : undefined;
      const alertLabel = isMuted
        ? atPlayhead
          ? "Muted at playhead"
          : "Muted on lane"
        : !hasMediaUrl
          ? "Direct audio missing"
          : bindingMode === "overrideAudio"
            ? "Audio override engaged"
            : undefined;

    return {
      text: [
          witnessLabelSummary,
          memberSummaryLabel,
          familyClipCountLabel,
          bindingMode === "overrideAudio" ? "Override stem riding picture" : "Sonic witness armed",
          alertLabel,
          layoutLabel,
        sampleRateLabel,
        durationLabel,
          editorialCueLabel,
        activeLabel,
      ]
        .filter(Boolean)
        .join("  •  "),
        tone: !hasMediaUrl ? "red" : isMuted || bindingMode === "overrideAudio" ? "orange" : "blue",
    };
  }

  if (mediaType === "image") {
      const readinessLabel = hasMediaUrl ? "Frame review ready" : "Still source missing";
    return {
        text: [
          witnessLabelSummary,
          memberSummaryLabel,
          familyClipCountLabel,
          "Still frame witness",
          hasLinkedVideo ? "Linked visual family" : undefined,
          durationLabel ? `Hold ${durationLabel}` : undefined,
          editorialCueLabel,
          activeLabel,
          readinessLabel,
        ]
          .filter(Boolean)
          .join("  •  "),
        tone: hasMediaUrl ? "blue" : "red",
    };
  }

  return { text: undefined, tone: "blue" };
}

interface ResolvePlaybackProjectionArgs {
  clips: MediaUnionClipLike[];
  mediaFamilies?: MediaFamily[];
  mediaMembers?: MediaMember[];
  editorialClipBindings?: EditorialClipBinding[];
  mediaMetadataByClipId?: Record<string, ClipMediaMetadata>;
  playheadSec: number;
  auditionMode: AuditionMode;
  selectedClipId?: string | null;
  requestedClipId?: string | null;
}

interface BindingCreationArgs {
  clipId: string;
  family: MediaFamily;
  mediaMembers: MediaMember[];
  audioMemberId?: string;
  videoMemberId?: string;
  source: EditorialBindingSource;
  existingBinding?: EditorialClipBinding | null;
}

const EPS = 1e-6;

function sortClips(clips: MediaUnionClipLike[]): MediaUnionClipLike[] {
  return [...clips].sort((left, right) => {
    const startDelta = (readClipStartSec(left) ?? 0) - (readClipStartSec(right) ?? 0);
    if (Math.abs(startDelta) > EPS) return startDelta;
    return readClipId(left)?.localeCompare(readClipId(right) ?? "") ?? 0;
  });
}

function sortMembers(members: MediaMember[]): MediaMember[] {
  return [...members].sort((left, right) => left.id.localeCompare(right.id));
}

function sortFamilies(families: MediaFamily[]): MediaFamily[] {
  return [...families].sort((left, right) => left.id.localeCompare(right.id));
}

export function parseMaybeNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

export function readClipId(clip: MediaUnionClipLike | null | undefined): string | null {
  const clipId = String(clip?.id ?? clip?.clipId ?? "").trim();
  return clipId || null;
}

export function readClipStartSec(clip: MediaUnionClipLike | null | undefined): number | null {
  return parseMaybeNumber(clip?.startSec ?? clip?.start);
}

export function readClipEndSec(clip: MediaUnionClipLike | null | undefined): number | null {
  const end = parseMaybeNumber(clip?.endSec ?? clip?.end);
  if (end != null) return end;

  const start = readClipStartSec(clip);
  const dur = parseMaybeNumber(clip?.durSec ?? clip?.dur);
  if (start == null || dur == null) return null;
  return start + dur;
}

export function readClipDurationSec(clip: MediaUnionClipLike | null | undefined): number | undefined {
  const start = readClipStartSec(clip);
  const end = readClipEndSec(clip);
  if (start == null || end == null) return undefined;
  const durationSec = Math.max(0, end - start);
  return durationSec > 0 ? durationSec : undefined;
}

export function readClipMediaUrl(clip: MediaUnionClipLike | null | undefined): string | undefined {
  const mediaUrl = String(
    clip?.mediaUrl ?? clip?.previewUrl ?? clip?.url ?? clip?.src ?? clip?.media?.url ?? "",
  ).trim();
  return mediaUrl || undefined;
}

function readClipMediaId(clip: MediaUnionClipLike | null | undefined): string | undefined {
  const mediaId = String(clip?.mediaId ?? clip?.media?.id ?? "").trim();
  return mediaId || undefined;
}

function readClipRequestId(clip: MediaUnionClipLike | null | undefined): string | undefined {
  const requestId = String(clip?.requestId ?? "").trim();
  return requestId || undefined;
}

function readClipMuted(clip: MediaUnionClipLike | null | undefined): boolean {
  return Boolean(clip?.muted);
}

function readMediaKind(clip: MediaUnionClipLike | null | undefined, metadata?: ClipMediaMetadata): MediaUnionKind {
  const kind = String(metadata?.kind ?? clip?.kind ?? "").trim().toLowerCase();
  if (kind === "audio" || kind === "video" || kind === "image") return kind;
  return "unknown";
}

function readClipLabel(clip: MediaUnionClipLike | null | undefined, metadata?: ClipMediaMetadata): string {
  return (
    String(
      metadata?.sourceLabel ??
        metadata?.fileName ??
        clip?.title ??
        clip?.label ??
        clip?.name ??
        clip?.assetName ??
        clip?.mediaName ??
        clip?.fileName ??
        clip?.filename ??
        readClipMediaId(clip) ??
        readClipMediaUrl(clip) ??
        "",
    ).trim() || `${readMediaKind(clip, metadata)} source`
  );
}

function sanitizeIdentityPart(value: string): string {
  return value.replace(/[^a-z0-9:_-]+/gi, "-").replace(/-+/g, "-").replace(/^-|-$/g, "") || "unknown";
}

function readStoragePath(metadata?: ClipMediaMetadata): string | undefined {
  const storagePath = String(
    metadata?.storagePath ??
      metadata?.metadata?.storagePath ??
      metadata?.metadata?.path ??
      metadata?.metadata?.objectPath ??
      "",
  ).trim();
  return storagePath || undefined;
}

function readStableLabelFingerprint(clip: MediaUnionClipLike, metadata?: ClipMediaMetadata): string | undefined {
  const labelSeed = String(
    metadata?.fileName ?? metadata?.sourceLabel ?? clip?.fileName ?? clip?.filename ?? clip?.assetName ?? clip?.mediaName ?? "",
  ).trim();
  if (!labelSeed) return undefined;

  const durationSec = metadata?.durationSec ?? readClipDurationSec(clip) ?? 0;
  const roundedDurationSec = Number.isFinite(durationSec) && durationSec > 0 ? Math.round(durationSec * 1000) : 0;
  return `${readMediaKind(clip, metadata)}:${sanitizeIdentityPart(labelSeed)}:${roundedDurationSec}`;
}

function readStableSourceSeed(clip: MediaUnionClipLike, metadata?: ClipMediaMetadata): string | undefined {
  return (
    String(metadata?.sourceAssetId ?? "").trim() ||
    readStoragePath(metadata) ||
    readClipMediaId(clip) ||
    readStableLabelFingerprint(clip, metadata) ||
    readClipRequestId(clip) ||
    undefined
  );
}

function normalizeMediaUrlForIdentity(mediaUrl: string | undefined): string | undefined {
  const raw = String(mediaUrl ?? "").trim();
  if (!raw) return undefined;

  try {
    const parsed = new URL(raw, "https://omnicine.local");
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return raw.split("?")[0]?.split("#")[0]?.trim() || raw;
  }
}

function makeIdentityKey(clip: MediaUnionClipLike, metadata?: ClipMediaMetadata): string {
  const explicitFamilyId = String(metadata?.familyId ?? "").trim();
  if (explicitFamilyId) return `family:${explicitFamilyId}`;

  const stableSourceSeed = readStableSourceSeed(clip, metadata);
  if (stableSourceSeed) return `asset:${stableSourceSeed}`;

  const mediaUrl = normalizeMediaUrlForIdentity(readClipMediaUrl(clip) ?? metadata?.mediaUrl);
  if (mediaUrl) return `url:${mediaUrl}`;

  return `clip:${readClipId(clip) ?? "unknown"}`;
}

function makeFamilyId(identityKey: string): string {
  return `family:${sanitizeIdentityPart(identityKey)}`;
}

function makeMemberId(familyId: string, kind: MediaMemberKind, seed: string): string {
  return `${familyId}:${kind}:${sanitizeIdentityPart(seed)}`;
}

function clipOverlapsPlayhead(clip: MediaUnionClipLike, playheadSec: number): boolean {
  const startSec = readClipStartSec(clip);
  const endSec = readClipEndSec(clip);
  if (startSec == null || endSec == null) return false;
  return playheadSec >= startSec - EPS && playheadSec <= endSec + EPS;
}

function chooseClip(
  candidates: MediaUnionClipLike[],
  playheadSec: number,
  preferredClipId?: string | null,
): MediaUnionClipLike | null {
  const preferredId = String(preferredClipId ?? "").trim();
  if (preferredId) {
    const preferred = candidates.find((clip) => readClipId(clip) === preferredId);
    if (preferred) return preferred;
  }
  return candidates.find((clip) => clipOverlapsPlayhead(clip, playheadSec)) ?? candidates[0] ?? null;
}

function pickFocusClip(
  clips: MediaUnionClipLike[],
  playheadSec: number,
  requestedClipId?: string | null,
  selectedClipId?: string | null,
): MediaUnionClipLike | null {
  const requestedIds = [requestedClipId, selectedClipId]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);

  for (const candidateId of requestedIds) {
    const hit = clips.find((clip) => readClipId(clip) === candidateId);
    if (hit) return hit;
  }

  return chooseClip(clips, playheadSec);
}

function inferOriginType(kind: MediaUnionKind, metadata?: ClipMediaMetadata): MediaFamilyOrigin {
  if (metadata?.originType) return metadata.originType;
  if (kind === "audio") return "audioOnly";
  if (kind === "video") {
    if (metadata?.hasPlayableAudio === false) return "videoOnly";
    return "videoWithEmbeddedAudio";
  }
  return "linkedMedia";
}

function inferHasPlayableAudio(kind: MediaUnionKind, metadata?: ClipMediaMetadata): boolean {
  if (kind !== "video") return false;
  if (metadata?.originType === "videoOnly") return false;
  if (typeof metadata?.hasPlayableAudio === "boolean") return metadata.hasPlayableAudio;
  return true;
}

function shouldCreateWaveformProxy(metadata?: ClipMediaMetadata): boolean {
  return Boolean(String(metadata?.waveformProxyUrl ?? "").trim());
}

function shouldCreatePreviewProxy(metadata?: ClipMediaMetadata): boolean {
  return Boolean(String(metadata?.previewProxyUrl ?? "").trim());
}

function upsertMember(memberMap: Map<string, MediaMember>, nextMember: MediaMember): MediaMember {
  const existing = memberMap.get(nextMember.id);
  if (!existing) {
    memberMap.set(nextMember.id, nextMember);
    return nextMember;
  }

  const clipIds = Array.from(new Set([...existing.clipIds, ...nextMember.clipIds])).sort();
  const merged: MediaMember = {
    ...existing,
    ...nextMember,
    durationSec: Math.max(existing.durationSec || 0, nextMember.durationSec || 0),
    metadata: { ...(existing.metadata ?? {}), ...(nextMember.metadata ?? {}) },
    clipIds,
  };

  memberMap.set(nextMember.id, merged);
  return merged;
}

function finalizeFamilyOriginType(family: MediaFamily, familyMembers: MediaMember[]): MediaFamilyOrigin {
  const hasVideo = familyMembers.some((member) => member.kind === "video");
  const hasEmbeddedAudio = familyMembers.some((member) => member.kind === "embeddedAudio");
  const hasExternalAudio = familyMembers.some((member) => member.kind === "audio");

  if (hasVideo && hasExternalAudio) return "linkedMedia";
  if (hasVideo && hasEmbeddedAudio) return "videoWithEmbeddedAudio";
  if (hasVideo) return "videoOnly";
  return "audioOnly";
}

function selectPreferredAudioMemberId(familyMembers: MediaMember[]): string | undefined {
  return (
    familyMembers.find((member) => member.kind === "embeddedAudio" && member.isPlayable)?.id ??
    familyMembers.find((member) => member.kind === "audio" && member.isPlayable)?.id
  );
}

function selectPreferredVideoMemberId(familyMembers: MediaMember[]): string | undefined {
  return familyMembers.find((member) => member.kind === "video" && member.isPlayable)?.id;
}

function buildFamilyMember(
  familyId: string,
  clip: MediaUnionClipLike,
  metadata: ClipMediaMetadata | undefined,
  kind: MediaMemberKind,
  seed: string,
  opts?: Partial<Pick<MediaMember, "createdFromMemberId" | "isPlayable" | "isVisible">>,
): MediaMember {
  const clipId = readClipId(clip);
  const sourceAssetId =
    String(readStableSourceSeed(clip, metadata) ?? normalizeMediaUrlForIdentity(readClipMediaUrl(clip) ?? metadata?.mediaUrl) ?? clipId ?? kind).trim();
  const durationSec = metadata?.durationSec ?? readClipDurationSec(clip) ?? 0;
  const mediaUrl =
    kind === "waveformProxy"
      ? String(metadata?.waveformProxyUrl ?? "").trim() || undefined
      : kind === "previewProxy"
        ? String(metadata?.previewProxyUrl ?? "").trim() || undefined
        : metadata?.mediaUrl ?? readClipMediaUrl(clip);

  return {
    id: makeMemberId(familyId, kind, seed || sourceAssetId || kind),
    familyId,
    kind,
    sourceAssetId,
    label: readClipLabel(clip, metadata),
    durationSec: Math.max(0, durationSec),
    sampleRate: metadata?.sampleRate,
    channelLayout: metadata?.channelLayout,
    frameRate: metadata?.frameRate,
    createdFromMemberId: opts?.createdFromMemberId,
    isPlayable: opts?.isPlayable ?? (kind !== "waveformProxy" && kind !== "previewProxy"),
    isVisible: opts?.isVisible ?? (kind === "video" || kind === "previewProxy" || kind === "waveformProxy"),
    metadata: metadata?.metadata,
    mediaUrl,
    requestId: readClipRequestId(clip),
    clipIds: clipId ? [clipId] : [],
  };
}

export function deriveMediaRegistryFromTimeline(
  clips: MediaUnionClipLike[],
  mediaMetadataByClipId: Record<string, ClipMediaMetadata> = {},
): DerivedMediaRegistry {
  const familyMap = new Map<string, MediaFamily>();
  const memberMap = new Map<string, MediaMember>();
  const familyIdByClipId: Record<string, string> = {};

  for (const clip of sortClips(clips)) {
    const clipId = readClipId(clip);
    if (!clipId) continue;

    const metadata = mediaMetadataByClipId[clipId];
    const identityKey = makeIdentityKey(clip, metadata);
    const familyId = makeFamilyId(identityKey);
    const originType = inferOriginType(readMediaKind(clip, metadata), metadata);

    familyIdByClipId[clipId] = familyId;

    const family = familyMap.get(familyId) ?? {
      id: familyId,
      primaryLabel: readClipLabel(clip, metadata),
      originType,
      memberIds: [],
      preferredVideoMemberId: undefined,
      preferredAudioMemberId: undefined,
      createdAt: 0,
      clipIds: [],
      identityKey,
    };

    if (!family.clipIds.includes(clipId)) family.clipIds.push(clipId);
    if (!familyMap.has(familyId)) familyMap.set(familyId, family);

    const mediaKind = readMediaKind(clip, metadata);
    const memberSeed = String(readStableSourceSeed(clip, metadata) ?? clipId).trim();
    const familyMembers: MediaMember[] = [];

    if (mediaKind === "audio") {
      familyMembers.push(buildFamilyMember(familyId, clip, metadata, "audio", memberSeed, { isVisible: false }));
    }

    if (mediaKind === "video" || mediaKind === "image") {
      const videoMember = buildFamilyMember(familyId, clip, metadata, "video", memberSeed, { isVisible: true });
      familyMembers.push(videoMember);

      if (mediaKind === "video" && inferHasPlayableAudio(mediaKind, metadata)) {
        familyMembers.push(
          buildFamilyMember(familyId, clip, metadata, "embeddedAudio", `${memberSeed}:embedded`, {
            createdFromMemberId: videoMember.id,
            isVisible: false,
          }),
        );
      }

      if (mediaKind === "video" && shouldCreateWaveformProxy(metadata)) {
        familyMembers.push(
          buildFamilyMember(familyId, clip, metadata, "waveformProxy", `${memberSeed}:waveform`, {
            createdFromMemberId: videoMember.id,
            isPlayable: false,
            isVisible: true,
          }),
        );
      }

      if (mediaKind === "video" && shouldCreatePreviewProxy(metadata)) {
        familyMembers.push(
          buildFamilyMember(familyId, clip, metadata, "previewProxy", `${memberSeed}:preview`, {
            createdFromMemberId: videoMember.id,
            isPlayable: false,
            isVisible: true,
          }),
        );
      }
    }

    for (const member of familyMembers) {
      const inserted = upsertMember(memberMap, member);
      if (!family.memberIds.includes(inserted.id)) family.memberIds.push(inserted.id);
    }
  }

  const members = sortMembers(Array.from(memberMap.values()));
  const membersByFamilyId = new Map<string, MediaMember[]>();
  for (const member of members) {
    const list = membersByFamilyId.get(member.familyId) ?? [];
    list.push(member);
    membersByFamilyId.set(member.familyId, list);
  }

  const families = sortFamilies(
    Array.from(familyMap.values()).map((family) => {
      const familyMembers = sortMembers(membersByFamilyId.get(family.id) ?? []);
      return {
        ...family,
        memberIds: familyMembers.map((member) => member.id),
        originType: finalizeFamilyOriginType(family, familyMembers),
        preferredAudioMemberId: selectPreferredAudioMemberId(familyMembers),
        preferredVideoMemberId: selectPreferredVideoMemberId(familyMembers),
        clipIds: [...family.clipIds].sort(),
      };
    }),
  );

  return {
    families,
    members,
    familyIdByClipId,
  };
}

function resolveFamilyMembers(mediaMembers: MediaMember[], familyId: string): MediaMember[] {
  return sortMembers(mediaMembers.filter((member) => member.familyId === familyId));
}

function resolveRequestedAudioMemberId(
  family: MediaFamily,
  mediaMembers: MediaMember[],
  requestedAudioMemberId?: string,
): string | undefined {
  const familyMembers = resolveFamilyMembers(mediaMembers, family.id);
  if (
    requestedAudioMemberId &&
    familyMembers.some(
      (member) =>
        member.id === requestedAudioMemberId &&
        member.isPlayable &&
        (member.kind === "audio" || member.kind === "embeddedAudio"),
    )
  ) {
    return requestedAudioMemberId;
  }

  return family.preferredAudioMemberId;
}

function resolveRequestedVideoMemberId(
  family: MediaFamily,
  mediaMembers: MediaMember[],
  requestedVideoMemberId?: string,
): string | undefined {
  const familyMembers = resolveFamilyMembers(mediaMembers, family.id);
  if (
    requestedVideoMemberId &&
    familyMembers.some((member) => member.id === requestedVideoMemberId && member.kind === "video" && member.isPlayable)
  ) {
    return requestedVideoMemberId;
  }

  return family.preferredVideoMemberId;
}

export function deriveEditorialBindingMode(
  family: MediaFamily,
  audioMemberId?: string,
  videoMemberId?: string,
): EditorialBindingMode {
  if (audioMemberId && videoMemberId) {
    if (
      family.preferredAudioMemberId &&
      family.preferredVideoMemberId &&
      audioMemberId === family.preferredAudioMemberId &&
      videoMemberId === family.preferredVideoMemberId
    ) {
      return "linked";
    }
    return "overrideAudio";
  }

  if (audioMemberId) return "audioOnly";
  return "videoOnly";
}

export function createEditorialClipBinding({
  clipId,
  family,
  mediaMembers,
  audioMemberId,
  videoMemberId,
  source,
  existingBinding,
}: BindingCreationArgs): EditorialClipBinding {
  const resolvedAudioMemberId = resolveRequestedAudioMemberId(family, mediaMembers, audioMemberId);
  const resolvedVideoMemberId = resolveRequestedVideoMemberId(family, mediaMembers, videoMemberId);
  const safeSource = source === "explicit" ? "explicit" : "default";
  const ts = existingBinding?.updatedAt ?? 0;

  return {
    clipId,
    familyId: family.id,
    videoMemberId: resolvedVideoMemberId,
    audioMemberId: resolvedAudioMemberId,
    bindingMode: deriveEditorialBindingMode(family, resolvedAudioMemberId, resolvedVideoMemberId),
    createdAt: existingBinding?.createdAt ?? ts,
    updatedAt: ts,
    source: safeSource,
  };
}

function buildDefaultBinding(
  clipId: string,
  family: MediaFamily,
  mediaMembers: MediaMember[],
  existingBinding?: EditorialClipBinding | null,
): EditorialClipBinding {
  return createEditorialClipBinding({
    clipId,
    family,
    mediaMembers,
    audioMemberId: family.preferredAudioMemberId,
    videoMemberId: family.preferredVideoMemberId,
    source: "default",
    existingBinding,
  });
}

export function reconcileEditorialClipBindings(args: {
  clips: MediaUnionClipLike[];
  mediaFamilies: MediaFamily[];
  mediaMembers: MediaMember[];
  existingBindings: EditorialClipBinding[];
}): EditorialClipBinding[] {
  const { clips, mediaFamilies, mediaMembers, existingBindings } = args;
  const familyById = new Map(mediaFamilies.map((family) => [family.id, family]));
  const familyIdByClipId: Record<string, string> = {};
  for (const family of mediaFamilies) {
    for (const clipId of family.clipIds) familyIdByClipId[clipId] = family.id;
  }

  const existingByClipId = new Map(existingBindings.map((binding) => [binding.clipId, binding]));
  const nextBindings: EditorialClipBinding[] = [];

  for (const clip of sortClips(clips)) {
    const clipId = readClipId(clip);
    if (!clipId) continue;

    const familyId = familyIdByClipId[clipId];
    const family = familyId ? familyById.get(familyId) : null;
    if (!family) continue;

    const existing = existingByClipId.get(clipId) ?? null;
    if (existing?.source === "explicit") {
      nextBindings.push(
        createEditorialClipBinding({
          clipId,
          family,
          mediaMembers,
          audioMemberId: existing.audioMemberId,
          videoMemberId: existing.videoMemberId,
          source: "explicit",
          existingBinding: existing,
        }),
      );
      continue;
    }

    nextBindings.push(buildDefaultBinding(clipId, family, mediaMembers, existing));
  }

  return nextBindings;
}

function buildBindingLookup(editorialClipBindings: EditorialClipBinding[]): Map<string, EditorialClipBinding> {
  return new Map(editorialClipBindings.map((binding) => [binding.clipId, binding]));
}

function buildRegistryFromArgs(args: ResolvePlaybackProjectionArgs): {
  families: MediaFamily[];
  members: MediaMember[];
  bindings: EditorialClipBinding[];
  familyIdByClipId: Record<string, string>;
} {
  const registry =
    args.mediaFamilies && args.mediaMembers
      ? {
          families: sortFamilies(args.mediaFamilies),
          members: sortMembers(args.mediaMembers),
          familyIdByClipId: Object.fromEntries(
            args.mediaFamilies.flatMap((family) => family.clipIds.map((clipId) => [clipId, family.id])),
          ),
        }
      : deriveMediaRegistryFromTimeline(args.clips, args.mediaMetadataByClipId);

  const bindings = args.editorialClipBindings
    ? reconcileEditorialClipBindings({
        clips: args.clips,
        mediaFamilies: registry.families,
        mediaMembers: registry.members,
        existingBindings: args.editorialClipBindings,
      })
    : reconcileEditorialClipBindings({
        clips: args.clips,
        mediaFamilies: registry.families,
        mediaMembers: registry.members,
        existingBindings: [],
      });

  return {
    families: registry.families,
    members: registry.members,
    bindings,
    familyIdByClipId: registry.familyIdByClipId,
  };
}

function resolveMemberSummary(
  family: MediaFamily | null,
  binding: EditorialClipBinding | null,
  mediaMembers: MediaMember[],
): string | undefined {
  if (!family || !binding) return undefined;

  const videoMember = binding.videoMemberId ? mediaMembers.find((member) => member.id === binding.videoMemberId) : null;
  const audioMember = binding.audioMemberId ? mediaMembers.find((member) => member.id === binding.audioMemberId) : null;

  if (binding.bindingMode === "videoOnly") return "Video only";
  if (binding.bindingMode === "audioOnly") return audioMember ? `Audio: ${audioMember.label}` : "Audio only";
  if (binding.bindingMode === "overrideAudio") {
    return `Video: ${videoMember?.label ?? family.primaryLabel} • Audio override: ${audioMember?.label ?? "custom"}`;
  }
  return `Video: ${videoMember?.label ?? family.primaryLabel} • Audio: ${audioMember?.label ?? "linked"}`;
}

export function formatWitnessLabel(
  family: MediaFamily | null,
  binding: EditorialClipBinding | null,
  mediaMembers: MediaMember[],
): string | undefined {
  if (!family || !binding) return undefined;
  const prefix = binding.videoMemberId ? "VIDEO" : "AUDIO";

  switch (binding.bindingMode) {
    case "audioOnly":
      return `AUDIO • ${family.primaryLabel}`;
    case "videoOnly":
      return `VIDEO • ${family.primaryLabel} • Silent`;
    case "overrideAudio": {
      const audioMember = binding.audioMemberId ? mediaMembers.find((member) => member.id === binding.audioMemberId) : null;
      return `VIDEO • ${family.primaryLabel} • Override Audio${audioMember ? ` (${audioMember.label})` : ""}`;
    }
    default:
      return `${prefix} • ${family.primaryLabel} • Linked Audio`;
  }
}

function buildAudioPlayback(
  clip: MediaUnionClipLike,
  binding: EditorialClipBinding,
  family: MediaFamily,
  audioMember: MediaMember,
  mode: AuditionMode,
): ResolvedAudioPlayback {
  const clipId = readClipId(clip) ?? binding.clipId;
  const clipStartSec = readClipStartSec(clip) ?? 0;
  const clipEndSec = readClipEndSec(clip) ?? clipStartSec;
  const sourceDurationSec = Math.max(0, audioMember.durationSec || readClipDurationSec(clip) || 0);

  return {
    clipId,
    familyId: family.id,
    audioMemberId: audioMember.id,
    startSec: mode === "source" ? 0 : clipStartSec,
    endSec: mode === "source" ? sourceDurationSec : clipEndSec,
    muted: readClipMuted(clip),
    label: audioMember.label,
    sourceAssetId: audioMember.sourceAssetId,
    memberKind: audioMember.kind,
  };
}

function buildVideoPlayback(
  clip: MediaUnionClipLike,
  binding: EditorialClipBinding,
  family: MediaFamily,
  videoMember: MediaMember,
  mode: AuditionMode,
): ResolvedVideoPlayback {
  const clipId = readClipId(clip) ?? binding.clipId;
  const clipStartSec = readClipStartSec(clip) ?? 0;
  const clipEndSec = readClipEndSec(clip) ?? clipStartSec;
  const sourceDurationSec = Math.max(0, videoMember.durationSec || readClipDurationSec(clip) || 0);

  return {
    clipId,
    familyId: family.id,
    videoMemberId: videoMember.id,
    startSec: mode === "source" ? 0 : clipStartSec,
    endSec: mode === "source" ? sourceDurationSec : clipEndSec,
    visible: true,
    label: videoMember.label,
    sourceAssetId: videoMember.sourceAssetId,
  };
}

export function resolvePlaybackProjection(args: ResolvePlaybackProjectionArgs): PlaybackProjection {
  const { clips, playheadSec, auditionMode, requestedClipId, selectedClipId } = args;
  const { families, members, bindings, familyIdByClipId } = buildRegistryFromArgs(args);
  const familyById = new Map(families.map((family) => [family.id, family]));
  const memberById = new Map(members.map((member) => [member.id, member]));
  const bindingByClipId = buildBindingLookup(bindings);
  const clipsSorted = sortClips(clips);
  const focusClip = pickFocusClip(clipsSorted, playheadSec, requestedClipId, selectedClipId);
  const focusClipId = readClipId(focusClip) ?? undefined;
  const focusBinding = focusClipId ? bindingByClipId.get(focusClipId) ?? null : null;
  const focusFamilyId = focusBinding?.familyId ?? (focusClipId ? familyIdByClipId[focusClipId] : undefined);
  const focusFamily = focusFamilyId ? familyById.get(focusFamilyId) ?? null : null;

  const projection: PlaybackProjection = {
    playheadSec,
    activeVideo: [],
    activeAudio: [],
    focusClipId,
    focusFamilyId,
    familyLabel: focusFamily?.primaryLabel,
    bindingMode: focusBinding?.bindingMode ?? "unbound",
    bindingSource: focusBinding?.source ?? "none",
    hasLinkedAudio: Boolean(focusFamily?.preferredAudioMemberId),
    hasLinkedVideo: Boolean(focusFamily?.preferredVideoMemberId),
    witnessLabel: formatWitnessLabel(focusFamily, focusBinding, members),
    resolvedMemberSummary: resolveMemberSummary(focusFamily, focusBinding, members),
  };

  const clipsToResolve =
    auditionMode === "sequence"
      ? clipsSorted.filter((clip) => clipOverlapsPlayhead(clip, playheadSec))
      : focusClip
        ? [focusClip]
        : [];

  for (const clip of clipsToResolve) {
    const clipId = readClipId(clip);
    if (!clipId) continue;

    const binding = bindingByClipId.get(clipId);
    if (!binding) continue;

    const family = familyById.get(binding.familyId);
    if (!family) continue;

    if (!readClipMuted(clip) && binding.audioMemberId) {
      const audioMember = memberById.get(binding.audioMemberId);
      if (audioMember?.isPlayable) {
        projection.activeAudio.push(buildAudioPlayback(clip, binding, family, audioMember, auditionMode));
      }
    }

    if (binding.videoMemberId) {
      const videoMember = memberById.get(binding.videoMemberId);
      if (videoMember?.isPlayable) {
        projection.activeVideo.push(buildVideoPlayback(clip, binding, family, videoMember, auditionMode));
      }
    }
  }

  projection.activeAudio.sort((left, right) => left.startSec - right.startSec || left.clipId.localeCompare(right.clipId));
  projection.activeVideo.sort((left, right) => left.startSec - right.startSec || left.clipId.localeCompare(right.clipId));

  const firstAudio = projection.activeAudio[0];
  const firstVideo = projection.activeVideo[0];
  projection.activeAudioMemberId = firstAudio?.audioMemberId;
  projection.activeVideoMemberId = firstVideo?.videoMemberId;
  projection.activeAudioClipId = firstAudio?.clipId;
  projection.activeVideoClipId = firstVideo?.clipId;
  projection.activeAudioDurationSec = firstAudio ? Math.max(0, firstAudio.endSec - firstAudio.startSec) : undefined;
  projection.activeVideoDurationSec = firstVideo ? Math.max(0, firstVideo.endSec - firstVideo.startSec) : undefined;

  return projection;
}

export function deriveMediaBindingEditorState(args: ResolvePlaybackProjectionArgs): MediaBindingEditorState {
  const { clips, playheadSec, requestedClipId, selectedClipId } = args;
  const { families, members, bindings, familyIdByClipId } = buildRegistryFromArgs(args);
  const familyById = new Map(families.map((family) => [family.id, family]));
  const clipsSorted = sortClips(clips);
  const focusClip = pickFocusClip(clipsSorted, playheadSec, requestedClipId, selectedClipId);
  const focusClipId = readClipId(focusClip) ?? undefined;
  const binding = focusClipId ? buildBindingLookup(bindings).get(focusClipId) ?? null : null;
  const selectedFamilyId = binding?.familyId ?? (focusClipId ? familyIdByClipId[focusClipId] : undefined);
  const selectedFamily = selectedFamilyId ? familyById.get(selectedFamilyId) ?? null : null;
  const familyOptions = families.map((family) => {
    const familyMembers = resolveFamilyMembers(members, family.id);
    return {
      id: family.id,
      label: family.primaryLabel,
      clipCount: family.clipIds.length,
      audioMemberCount: familyMembers.filter(
        (member) => member.isPlayable && (member.kind === "audio" || member.kind === "embeddedAudio"),
      ).length,
      videoMemberCount: familyMembers.filter((member) => member.isPlayable && member.kind === "video").length,
      originType: family.originType,
    };
  });
  const audioMembers = selectedFamily
    ? resolveFamilyMembers(members, selectedFamily.id).filter(
        (member) => member.isPlayable && (member.kind === "audio" || member.kind === "embeddedAudio"),
      )
    : [];
  const videoMembers = selectedFamily
    ? resolveFamilyMembers(members, selectedFamily.id).filter((member) => member.isPlayable && member.kind === "video")
    : [];

  return {
    focusClipId,
    bindingMode: binding?.bindingMode ?? "unbound",
    bindingSource: binding?.source ?? "none",
    selectedFamilyId,
    familyLabel: selectedFamily?.primaryLabel,
    selectedFamily: selectedFamily ?? undefined,
    families,
    members,
    familyOptions,
    audioMembers,
    videoMembers,
    preferredAudioMemberId: binding?.audioMemberId ?? selectedFamily?.preferredAudioMemberId,
    preferredVideoMemberId: binding?.videoMemberId ?? selectedFamily?.preferredVideoMemberId,
    hasLinkedAudio: Boolean(selectedFamily?.preferredAudioMemberId),
    hasLinkedVideo: Boolean(selectedFamily?.preferredVideoMemberId),
    witnessLabel: formatWitnessLabel(selectedFamily, binding, members),
    resolvedMemberSummary: resolveMemberSummary(selectedFamily, binding, members),
  };
}

export function buildImportWitnessCapsules(args: {
  clips: MediaUnionClipLike[];
  playheadSec: number;
  isPlaying: boolean;
  activeClipId?: string;
  mediaFamilies?: MediaFamily[];
  mediaMembers?: MediaMember[];
  editorialClipBindings?: EditorialClipBinding[];
  mediaMetadataByClipId?: Record<string, ClipMediaMetadata>;
}): ImportWitnessCapsuleSummary[] {
  const { clips, playheadSec, isPlaying, activeClipId } = args;
  const { families, members, bindings, familyIdByClipId } = buildRegistryFromArgs({
    clips,
    playheadSec,
    auditionMode: "sequence",
    mediaFamilies: args.mediaFamilies,
    mediaMembers: args.mediaMembers,
    editorialClipBindings: args.editorialClipBindings,
    mediaMetadataByClipId: args.mediaMetadataByClipId,
  });
  const familyById = new Map(families.map((family) => [family.id, family]));
  const bindingByClipId = buildBindingLookup(bindings);

  return sortClips(clips).flatMap((clip) => {
    const clipId = readClipId(clip);
    if (!clipId) return [];

    const binding = bindingByClipId.get(clipId) ?? null;
    const familyId = binding?.familyId ?? familyIdByClipId[clipId];
    const family = familyId ? familyById.get(familyId) ?? null : null;
    if (!binding || !family) return [];

    const metadata = args.mediaMetadataByClipId?.[clipId];

    const durationSec = readClipDurationSec(clip) ?? 0;
    const startSec = readClipStartSec(clip) ?? 0;
    const endSec = readClipEndSec(clip) ?? startSec;
    const atPlayhead = playheadSec >= startSec - EPS && playheadSec <= endSec + EPS;
    const label = formatWitnessLabel(family, binding, members) ?? family.primaryLabel;
    const resolvedMemberSummary = resolveMemberSummary(family, binding, members);
    const mediaKind = readMediaKind(clip, metadata);
    const mediaType =
      mediaKind === "image"
        ? "image"
        : mediaKind === "video" || Boolean(binding.videoMemberId)
          ? "video"
          : "audio";
    const sourceMemberId = binding.audioMemberId ?? binding.videoMemberId;
    const sourceMember = sourceMemberId ? members.find((member) => member.id === sourceMemberId) : null;
    const hasLinkedAudio = Boolean(family.preferredAudioMemberId);
    const hasLinkedVideo = Boolean(family.preferredVideoMemberId);
    const hasMediaUrl = Boolean(readClipMediaUrl(clip) ?? metadata?.mediaUrl);
    const currentTimeSec = atPlayhead ? Math.max(0, playheadSec - startSec) : undefined;
    const isMuted = readClipMuted(clip);
    const ticker = buildImportWitnessTicker({
      mediaType,
      witnessLabel: label,
      resolvedMemberSummary,
      familyClipCount: family.clipIds.length,
      bindingMode: binding.bindingMode,
      originType: metadata?.originType ?? family.originType,
      hasLinkedAudio,
      hasLinkedVideo,
      hasPlayableAudio: metadata?.hasPlayableAudio === true,
      hasMediaUrl,
      frameRate: metadata?.frameRate,
      sampleRate: metadata?.sampleRate,
      channelLayout: metadata?.channelLayout,
      durationSec,
      atPlayhead,
      isPlaying,
      isMuted,
      currentTimeSec,
      startSec,
      leadOffsetSec: atPlayhead ? 0 : Math.max(0, startSec - playheadSec),
      tailRemainingSec: atPlayhead ? Math.max(0, endSec - playheadSec) : undefined,
    });

    return [
      {
        clipId,
        familyId: family.id,
        mediaType,
        label,
        witnessLabel: label,
        resolvedMemberSummary,
        familyClipCount: family.clipIds.length,
        durationSec,
        currentTimeSec,
        status: !hasMediaUrl ? "error" : isMuted ? "muted" : atPlayhead && isPlaying ? "playing" : "ready",
        sourceId: sourceMember?.sourceAssetId ?? sourceMember?.id ?? clipId,
        tickerText: ticker.text,
        tickerTone: ticker.tone,
        bindingMode: binding.bindingMode,
        hasLinkedAudio,
        hasLinkedVideo,
      },
    ];
  });
}